import { useEffect, useMemo, useState } from 'react';
import { get } from 'aws-amplify/api';
import { signOut, getCurrentUser } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import './admin.css';

function fmt(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('en-GB');
}

// Display labels for the free-form paymentMethods values returned by /fleet.
const PAYMENT_LABELS = {
  iap: 'IAP',
  stripe: 'Stripe',
  credits: 'Credits',
  crypto: 'Crypto',
};

function PaymentBadges({ methods }) {
  if (!methods || methods.length === 0) {
    return <span className="admin-badge admin-badge--none">No payments</span>;
  }
  return (
    <span className="admin-badges">
      {methods.map((m) => (
        <span key={m} className={`admin-badge admin-badge--${m}`}>
          {PAYMENT_LABELS[m] || m}
        </span>
      ))}
    </span>
  );
}

const COLUMNS = [
  { key: 'name', label: 'App', type: 'string' },
  { key: 'payments', label: 'Payments', type: 'string' },
  { key: 'totalUsers', label: 'Total users', type: 'number' },
  { key: 'signups7d', label: 'Signups (7d)', type: 'number' },
  { key: 'signups30d', label: 'Signups (30d)', type: 'number' },
  { key: 'activeUsers24h', label: 'Active users (24h)', type: 'number' },
  { key: 'activeUsers7d', label: 'Active users (7d)', type: 'number' },
  { key: 'paying', label: 'Paying subs', type: 'number' },
  { key: 'comped', label: 'Comped', type: 'number' },
];

function sortValue(row, key) {
  if (key === 'payments') return (row.paymentMethods || []).join(',') || null;
  return row[key];
}

function compare(a, b, key, type, direction) {
  const av = sortValue(a, key);
  const bv = sortValue(b, key);
  // Nulls always sort last regardless of direction
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  let cmp;
  if (type === 'number') {
    cmp = av - bv;
  } else {
    cmp = String(av).localeCompare(String(bv));
  }
  return direction === 'asc' ? cmp : -cmp;
}

// App-specific extra metrics from /fleet (metrics object per app).
function extraMetrics(row) {
  if (!row.metrics) return null;
  const parts = [];
  if (row.metrics.lifetimeBuyers != null) parts.push(`${fmt(row.metrics.lifetimeBuyers)} lifetime buyers`);
  if (row.metrics.fundedUsers != null) parts.push(`${fmt(row.metrics.fundedUsers)} funded users`);
  for (const [k, v] of Object.entries(row.metrics)) {
    if (k !== 'lifetimeBuyers' && k !== 'fundedUsers') parts.push(`${k}: ${fmt(v)}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('totalUsers');
  const [sortDir, setSortDir] = useState('desc');

  const sortedApps = useMemo(() => {
    if (!data?.apps) return [];
    const col = COLUMNS.find((c) => c.key === sortKey);
    if (!col) return data.apps;
    return [...data.apps].sort((a, b) => compare(a, b, sortKey, col.type, sortDir));
  }, [data, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'payments' ? 'asc' : 'desc');
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await get({ apiName: 'wolftechAdminApi', path: '/fleet' }).response;
      setData(await res.body.json());
    } catch (e) {
      setError(e.message || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCurrentUser().then(setUser).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div>
          <h1 className="admin-header__title">WolfTech Admin Dashboard</h1>
          <p className="admin-header__subtitle">
            {user?.signInDetails?.loginId || user?.username || 'Signed in'} · {data?.generatedAt ? new Date(data.generatedAt).toLocaleString('en-GB') : ''}
          </p>
        </div>
        <div className="admin-header__actions">
          <button type="button" onClick={load} className="admin-btn admin-btn--secondary" disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button type="button" onClick={handleSignOut} className="admin-btn">
            Sign out
          </button>
        </div>
      </header>

      {error && (
        <div className="admin-alert" role="alert">
          {error}
        </div>
      )}

      {data?.totals && (
        <section className="admin-totals">
          <div className="admin-tile">
            <span className="admin-tile__label">Total users</span>
            <span className="admin-tile__value">{fmt(data.totals.totalUsers)}</span>
          </div>
          <div className="admin-tile">
            <span className="admin-tile__label">Signups (7d)</span>
            <span className="admin-tile__value">{fmt(data.totals.signups7d)}</span>
          </div>
          <div className="admin-tile">
            <span className="admin-tile__label">Signups (30d)</span>
            <span className="admin-tile__value">{fmt(data.totals.signups30d)}</span>
          </div>
          <div className="admin-tile">
            <span className="admin-tile__label">Active users (24h)</span>
            <span className="admin-tile__value">{fmt(data.totals.activeUsers24h)}</span>
          </div>
          <div className="admin-tile">
            <span className="admin-tile__label">Active users (7d)</span>
            <span className="admin-tile__value">{fmt(data.totals.activeUsers7d)}</span>
          </div>
          <div className="admin-tile">
            <span className="admin-tile__label">Paying subs</span>
            <span className="admin-tile__value">{fmt(data.totals.paying)}</span>
          </div>
          <div className="admin-tile">
            <span className="admin-tile__label">Monetising apps</span>
            <span className="admin-tile__value">{fmt(data.totals.monetisingApps)}</span>
          </div>
        </section>
      )}

      <section className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              {COLUMNS.map((col) => {
                const active = sortKey === col.key;
                const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '↕';
                return (
                  <th
                    key={col.key}
                    aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <button
                      type="button"
                      className={`admin-th-btn${active ? ' admin-th-btn--active' : ''}`}
                      onClick={() => handleSort(col.key)}
                    >
                      <span>{col.label}</span>
                      <span className="admin-th-arrow">{arrow}</span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && !data && (
              <tr>
                <td colSpan={COLUMNS.length} className="admin-table__empty">Loading stats…</td>
              </tr>
            )}
            {sortedApps.map((row) => {
              const extras = extraMetrics(row);
              return (
                <tr key={row.key} className={row.error ? 'admin-table__row--error' : ''}>
                  <td>
                    <div className="admin-app-name">{row.name}</div>
                    <div className="admin-app-pool">{row.poolId}</div>
                    {row.error && <div className="admin-app-error">{row.error}</div>}
                  </td>
                  <td className="admin-td-payments">
                    <PaymentBadges methods={row.paymentMethods} />
                  </td>
                  <td>{fmt(row.totalUsers)}</td>
                  <td>{fmt(row.signups7d)}</td>
                  <td>{fmt(row.signups30d)}</td>
                  <td>{fmt(row.activeUsers24h)}</td>
                  <td>{fmt(row.activeUsers7d)}</td>
                  <td>
                    {fmt(row.paying)}
                    {extras && <div className="admin-app-extra">{extras}</div>}
                  </td>
                  <td>{fmt(row.comped)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <footer className="admin-footer">
        <p>
          <strong>Note:</strong> Data comes from the combined <code>/fleet</code> endpoint covering every
          {' '}Wolf Technologies app. Totals and signup counts come from <code>ListUsers</code> with all
          {' '}internal <code>@wolftechnologies.co.uk</code> accounts (support@, review@, pete@, aliases)
          {' '}excluded. Active users (24h / 7d) counts
          {' '}current pool members whose Cognito <code>sub</code> appeared in a successful
          {' '}<code>InitiateAuth</code> event (via CloudTrail) within the window — covering both cold
          {' '}sign-ins and app re-opens (REFRESH_TOKEN_AUTH), so these are DAU / WAU metrics rather than
          {' '}raw sign-in event counts. Paying subs is a strict count from each app's DynamoDB table:
          {' '}status=active, trial ended, period not elapsed, not pending-cancel. Payment badges show each
          {' '}app's supported rails (IAP / Stripe / Credits / Crypto); credit and crypto apps report extra
          {' '}metrics (lifetime buyers, funded users) under Paying subs.
        </p>
      </footer>
    </div>
  );
}
